import React, { createContext, useContext, useEffect, useState } from 'react';

import loadScript from '@utils/loadScript';

import { useStore } from '@modules';
import useElementObserver from '@modules/support/hooks/useElementObserver';
import { handoffState } from '@modules/support/utils/handoffState';
import toggleStyleElement from '@modules/support/utils/toggleStyleElement';

import { ZendeskConstants } from '../constants';

const initialContext = {
  isOpen: false,
  isLoaded: false,
  open: () => {},
  close: () => {},
  setLang: () => {},
};

const Context = createContext<IZendeskContext>(initialContext);
const { ZENDESK_SCRIPT_ID, ZENDESK_SCRIPT_URL, ZD_MESSENGER_STYLE_ID } = ZendeskConstants;
const { getHandoffState, setHandoffActive } = handoffState;
const MESSENGER_ON_EVENT = 'messenger:on';

const ZendeskContext: CustomFC = ({ children }) => {
  const [{ global }] = useStore();
  const lang = global.lang;
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const close = () => window.zE('messenger', 'close');
  const open = () => window.zE('messenger', 'open');
  const setLang = () => window.zE('messenger:set', 'locale', lang);

  const handleWidgetVisibility = (showZendesk: boolean) => {
    if (showZendesk) {
      window.zE('messenger', 'show');
      toggleStyleElement(`#${ZD_MESSENGER_STYLE_ID}`, true);
    } else {
      window.zE('messenger', 'hide');
      window?.Forethought?.('widget', 'show');
      toggleStyleElement(`#${ZD_MESSENGER_STYLE_ID}`, false);
    }
  };

  useEffect(() => {
    loadScript(ZENDESK_SCRIPT_URL, ZENDESK_SCRIPT_ID).then(() => {
      setLang();
      close();
      setIsLoaded(true);
    });
  }, []);

  useElementObserver(`#${ZD_MESSENGER_STYLE_ID}`, () => {
    const currentHandoffState = getHandoffState();
    if (currentHandoffState) handleWidgetVisibility(true);
  });

  useEffect(() => {
    if (isLoaded) {
      window?.zE?.(MESSENGER_ON_EVENT, 'unreadMessages', count => {
        const currentHandoffState = getHandoffState();
        if (currentHandoffState && (currentHandoffState.unreadMessages > 0 || count > 0)) {
          setHandoffActive(count);
        }
      });

      window.zE(MESSENGER_ON_EVENT, 'open', () => {
        const currentHandoffState = getHandoffState();
        if (!currentHandoffState) handleWidgetVisibility(false);
        setIsOpen(true);
      });

      window.zE(MESSENGER_ON_EVENT, 'close', () => {
        const currentHandoffState = getHandoffState();
        if (!currentHandoffState) handleWidgetVisibility(false);
        setIsOpen(false);
      });
    }
  }, [isLoaded]);

  return (
    <Context.Provider value={{ open, close, setLang, isOpen, isLoaded }}>
      {children}
    </Context.Provider>
  );
};

export const useZendeskContext = () => useContext(Context);
export default ZendeskContext;
